/**
 * Product page: plugs the options panel into the Quick Personalizer
 * (design-templates) through its extension contract. The preview is rendered
 * by the quick personalizer; this only patches the template layers.
 */
import { createApp, h, reactive } from 'vue';
import './panel.tw.css';
import ConfiguratorPanel from './ConfiguratorPanel.vue';
import { buildPatches, expandPatch, resolveSelections } from './mappingEngine';
import { readSelections, writeSelections } from './selectionStore';
import { t } from './i18n';
import type { ConfiguratorProfile, Selections } from './types';

type DesignItemLike = { id: string; [key: string]: unknown };
type QuickApi = {
  invalidate: () => void;
  setProductColor: (index: number) => void;
  config: { smartConfigurator?: { enabled?: boolean; productId?: number; profile?: ConfiguratorProfile | null } };
  formatMoney: (amount: number) => string;
};
type QuickExtension = {
  id: string;
  mount?: (container: HTMLElement, api: QuickApi) => (() => void) | void;
  transformItems?: (items: Record<string, DesignItemLike[]>) => void;
  pricingContext?: () => unknown;
  validate?: () => string[];
};

const state = reactive({
  profile: null as ConfiguratorProfile | null,
  productId: 0,
  selections: {} as Selections,
  showErrors: false,
  lastColor: -1,
});

function resolved() {
  return resolveSelections(state.profile!, state.selections, (label) => t('Choose "{label}".').replace('{label}', label));
}

const extension: QuickExtension = {
  id: 'smart-configurator',
  mount(container, api) {
    const config = api.config.smartConfigurator;
    if (!config?.enabled || !config.profile?.options?.length) return;
    state.profile = config.profile;
    state.productId = Number(config.productId || 0);
    state.selections = readSelections(state.productId);

    const app = createApp({
      render: () => h(ConfiguratorPanel, {
        profile: state.profile!,
        modelValue: state.selections,
        money: api.formatMoney,
        showErrors: state.showErrors,
        heading: true,
        'onUpdate:modelValue': (next: Selections) => {
          state.selections = next;
          writeSelections(state.productId, next);
          const color = buildPatches(state.profile!, resolved()).find((patch) => 'type' in patch);
          if (color && 'colorIndex' in color && color.colorIndex !== state.lastColor) {
            state.lastColor = color.colorIndex;
            api.setProductColor(color.colorIndex);
          }
          api.invalidate();
        },
      }),
    });
    app.mount(container);
    return () => app.unmount();
  },
  transformItems(items) {
    if (!state.profile) return;
    const byId = new Map<string, DesignItemLike>();
    for (const list of Object.values(items)) {
      for (const item of list ?? []) byId.set(String(item.id), item);
    }
    for (const patch of buildPatches(state.profile, resolved())) {
      if ('layerId' in patch) {
        const item = byId.get(patch.layerId);
        if (item) Object.assign(item, expandPatch(item, patch.patch));
      }
    }
  },
  pricingContext: () => (state.profile ? { selections: resolved().selections } : undefined),
  validate() {
    if (!state.profile) return [];
    state.showErrors = true;
    return resolved().errors;
  },
};

const w = window as Window & { veyopQuickExtensions?: QuickExtension[] };
(w.veyopQuickExtensions ??= []).push(extension);
window.dispatchEvent(new Event('veyop:quick-extension'));
